import { FC, PropsWithChildren } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';
import { styled } from '@mui/material/styles';
import { Column } from '../Layout';
import Dropzone, { DropzoneProps } from './Dropzone';
import FileList, { FileListProps } from './FileList';
import FileRestrictionDetail, {
  FileRestrictionDetailProps,
} from './FileRestrictionDetail';

const ImageWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'spacing',
})<{ spacing: number }>(({ theme, spacing }) => ({
  borderRadius: theme.shape.borderRadius,
  borderColor: theme.palette.borderColor.main,
  borderStyle: 'solid',
  borderWidth: '1px',
  padding: theme.spacing(spacing, spacing),
}));

const InputTitleTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[900],
}));

const StyledFormHelperText = styled(FormHelperText)({
  padding: 0,
  margin: 0,
});

export type FileDropProps = PropsWithChildren<{
  inputTitle?: string;
  error?: boolean;
  helperText?: string;
  name?: string;
  hideDropzoneWhenPopulated?: boolean;
  spacing?: number;
}> &
  FileRestrictionDetailProps &
  FileListProps &
  DropzoneProps;

const FileDrop: FC<FileDropProps> = ({
  inputTitle,
  supportedFormats,
  files,
  sizeRestriction,
  maxFiles,
  disabled,
  onDeleteFile,
  onDropFile,
  name,
  height,
  error,
  helperText,
  hideDropzoneWhenPopulated,
  spacing = 3,
  children,
}) => {
  const isUploading = Boolean(files?.some((file) => file.isUploading));
  const hasFiles = Boolean(files?.length);
  const showDropzone = !(hideDropzoneWhenPopulated && hasFiles);

  return (
    <ImageWrapper spacing={spacing}>
      <Column spacing="sm">
        {inputTitle ? (
          <InputTitleTypography variant="body2">
            {inputTitle}
          </InputTitleTypography>
        ) : null}
        {showDropzone ? (
          <Dropzone
            name={name}
            height={height}
            maxFiles={maxFiles}
            sizeRestriction={sizeRestriction}
            disabled={isUploading || disabled}
            supportedFormats={supportedFormats}
            onDropFile={onDropFile}
          />
        ) : null}
        {hasFiles ? (
          <FileList
            files={files}
            onDeleteFile={disabled ? undefined : onDeleteFile}
          />
        ) : null}
        {sizeRestriction || supportedFormats?.length ? (
          <FileRestrictionDetail
            sizeRestriction={sizeRestriction}
            supportedFormats={supportedFormats}
          />
        ) : null}
        {helperText ? (
          <StyledFormHelperText error={error}>
            {helperText}
          </StyledFormHelperText>
        ) : null}
        {children ? (
          <>
            <Divider />
            {children}
          </>
        ) : null}
      </Column>
    </ImageWrapper>
  );
};

export default FileDrop;
